import styles from '../index.module.css';
import TCClient from '../../../../../client';
import { RenderSettingsParams } from './RenderSettings';
import { OrganizationSettings, UserSettings, UserOrganizationSettings } from 'admin/types';
import { BlockedByPersonaText } from 'admin/utils/shouldRender';

import {
  getTTLControlOptions,
  PINLOCK_TTL_CONTROL_OPTIONS,
} from 'admin/utils/commonControlOptions';

import { Slider } from 'admin/shared-components/Slider/Slider';
import { Dropdown } from 'admin/shared-components/Dropdown/Dropdown';
import { Checkbox, CheckboxProps } from 'admin/shared-components/Checkbox/Checkbox';
import {
  isIndividual,
  hasOrgFeatureOrSettings as hasFeature,
} from 'admin/utils/renderSettingConditions';

type RenderCheckBoxSettings = {
  userSettingKey: keyof UserSettings;
} & CheckboxProps;

export const userSettingsLabels = (
  userSettings: UserSettings,
  userOrgSettings: UserOrganizationSettings,
  setUserSettings: (us: UserSettings) => void,
  featureFlags: Record<string, unknown>,
  urlParams: Record<string, string>,
  forceDisableAll = false,
  version: 'v1' | 'v2' = 'v1',
  isLocked?: (settingName: string) => boolean,
  isPersonaForm = false
) => {
  const getIsIndividual = (setting: keyof UserOrganizationSettings) =>
    isIndividual(userOrgSettings, setting);
  const hasOrgFeatureOrSettings = (settings: Array<keyof UserOrganizationSettings>) =>
    hasFeature(userOrgSettings, settings);
  const allowTtlLowerThan3Days =
    !!featureFlags?.ALLOW_TTL_LOWER_THAN_3_DAYS || urlParams.allowTtlLowerThan3Days === 'true';
  const ttlOptions = getTTLControlOptions(allowTtlLowerThan3Days, userSettings?.messageLifespan);
  const initialTTLIndex = ttlOptions.findIndex(
    ({ value }) => value === userSettings?.messageLifespan
  );
  const isV2 = version === 'v2';

  const currentInitialTTLIndex = initialTTLIndex === -1 ? ttlOptions.length - 1 : initialTTLIndex;

  const renderCheckbox = (props: RenderCheckBoxSettings): JSX.Element => {
    const { userSettingKey, ...rest } = props;
    if (forceDisableAll) rest.disabled = true;
    if (isLocked && isLocked(userSettingKey)) rest.disabled = true;
    return (
      <Checkbox
        checked={!!userSettings[userSettingKey]}
        onClick={(v) => setUserSettings({ ...userSettings, [userSettingKey]: v })}
        {...rest}
      />
    );
  };

  const settings: RenderSettingsParams[] = [
    {
      label: 'Message Lifespan',
      lockDesc: isLocked?.('messageLifespan') ? BlockedByPersonaText : undefined,
      optInControl: (
        <Checkbox
          checked={!!userSettings.messageLifespanOptIn}
          disabled={forceDisableAll}
          label="Opt In"
          onClick={(v) =>
            setUserSettings({ ...userSettings, messageLifespanOptIn: v ? true : false })
          }
        />
      ),
      subLabel: 'Sent messages will be deleted after this time limit',
      span: true,
      settingControl: (
        <Slider
          onChange={(s) => setUserSettings({ ...userSettings, messageLifespan: s })}
          initialIndex={currentInitialTTLIndex}
          initialLabel={ttlOptions[currentInitialTTLIndex]?.label}
          options={ttlOptions}
          disabled={
            forceDisableAll ||
            (!userSettings.messageLifespanOptIn && isPersonaForm) ||
            (isLocked && isLocked('messageLifespan'))
          }
        />
      ),
    },
    {
      show: !!(
        urlParams.allowAdminSettingDeleteOnRead || featureFlags?.ALLOW_ADMIN_SETTING_DELETE_ON_READ
      ),
      label: 'Delete on Read',
      lockDesc: isLocked?.('isDeleteOnReadEnabled') ? BlockedByPersonaText : undefined,
      subLabel: 'All messages will be deleted one minute after opening',
      settingControl: renderCheckbox({
        userSettingKey: 'isDeleteOnReadEnabled',
        dataTestId: 'delete_on_read',
      }),
    },
    {
      label: 'PIN Lock (mobile)',
      lockDesc: isLocked?.('pinLock') ? BlockedByPersonaText : undefined,
      optInControl: (
        <Checkbox
          checked={!!userSettings.pinLockOptIn}
          disabled={forceDisableAll}
          label="Opt In"
          onClick={(v) => setUserSettings({ ...userSettings, pinLockOptIn: v ? true : false })}
        />
      ),
      span: true,
      subLabel:
        'Users are required to enter their PIN code on the mobile application after this time limit.',
      settingControl: (
        <Dropdown
          customCssClass={isV2 ? 'wide' : undefined}
          defaultValue={userSettings.pinLock}
          options={PINLOCK_TTL_CONTROL_OPTIONS}
          disabled={
            forceDisableAll ||
            (!userSettings.pinLockOptIn && isPersonaForm) ||
            (isLocked && isLocked('pinLock'))
          }
          onChange={(_, i) => {
            setUserSettings({
              ...userSettings,
              pinLock: parseInt(PINLOCK_TTL_CONTROL_OPTIONS[i].value, 10),
            });
          }}
        />
      ),
    },
    {
      label: 'Repeat Notifications',
      lockDesc: isLocked?.('areRepeatNotificationsEnabled') ? BlockedByPersonaText : undefined,
      subLabel:
        'Users will receive a new alert sound every 2 minutes, up to 20 minutes, until the message is viewed.',
      settingControl: renderCheckbox({
        userSettingKey: 'areRepeatNotificationsEnabled',
        dataTestId: 'repeat_notifications',
      }),
    },
    {
      label: 'Fast Deploy',
      lockDesc: isLocked?.('isFastDeployEnabled') ? BlockedByPersonaText : undefined,
      subLabel: 'Users not logged in will receive TigerConnect messages via email and SMS.',
      settingControl: renderCheckbox({
        userSettingKey: 'isFastDeployEnabled',
        dataTestId: 'fast_deploy',
      }),
    },
    {
      label: 'Message Forwarding',
      lockDesc: isLocked?.('isMessageForwardingEnabled') ? BlockedByPersonaText : undefined,
      subLabel: 'Enable users to forward TigerConnect messages to others within the organization.',
      settingControl: renderCheckbox({
        userSettingKey: 'isMessageForwardingEnabled',
        dataTestId: 'message_forwarding',
      }),
    },
    {
      hideFromPersonaForm:
        userOrgSettings.clickToCallSetting.clickToCallStatusSetting !== 'individual',
      requiredOrgSettings: ['featureClickToCall', 'clickToCallSetting'],
      label: 'Click to Call',
      lockDesc: isLocked?.('isClickToCallEnabled')
        ? BlockedByPersonaText
        : userOrgSettings.clickToCallSetting.clickToCallStatusSetting !== 'individual' && isV2
        ? 'Controlled by Admin'
        : undefined,
      subLabel:
        'Enable this feature to allow your users to one-tap call each other within TigerConnect. Only works for users with verified mobile numbers.',
      settingControl: (
        <div>
          {renderCheckbox({
            userSettingKey: 'isClickToCallEnabled',
            disabled: userOrgSettings.clickToCallSetting.clickToCallStatusSetting !== 'individual',
            dataTestId: 'click_to_call',
          })}
          {userOrgSettings.clickToCallSetting.clickToCallStatusSetting !== 'individual' &&
            !isV2 && <span className={styles.clickToCallAdmin}>Controlled by Admin</span>}
        </div>
      ),
    },
    {
      requiredOrgSettings: ['isVoipCallingEnabled'],
      label: 'VoIP Audio Calling',
      lockDesc: isLocked?.('isVoipCallingEnabled') ? BlockedByPersonaText : undefined,
      subLabel: 'Allow users to make and receive VoIP audio calls.',
      settingControl: renderCheckbox({
        userSettingKey: 'isVoipCallingEnabled',
        dataTestId: 'voip_audio_calling',
      }),
    },
    {
      forbiddenOffCondition: 'groupAudioCallingSetting',
      hideFromPersonaForm: !(
        hasOrgFeatureOrSettings(['isVoipCallingEnabled']) &&
        getIsIndividual('groupAudioCallingSetting')
      ),
      label: 'Group Audio Calling',
      lockDesc: isLocked?.('groupAudioCallingSetting') ? BlockedByPersonaText : undefined,
      subLabel: 'Allow users to make and receive group audio calls.',
      settingControl: renderCheckbox({
        userSettingKey: 'groupAudioCallingSetting',
        disabled: !(
          hasOrgFeatureOrSettings(['isVoipCallingEnabled']) &&
          getIsIndividual('groupAudioCallingSetting')
        ),
        dataTestId: 'group_audio_calling',
      }),
    },
    {
      requiredOrgSettings: ['isVideoCallEnabled'],
      label: 'Video Calling',
      lockDesc: isLocked?.('isVideoCallingEnabled') ? BlockedByPersonaText : undefined,
      subLabel: 'Allow users to make and receive video calls.',
      settingControl: renderCheckbox({
        userSettingKey: 'isVideoCallingEnabled',
        dataTestId: 'video_calling',
      }),
    },
    {
      forbiddenOffCondition: 'groupVideoCallingSetting',
      hideFromPersonaForm: !(
        userOrgSettings.isVideoCallEnabled && getIsIndividual('groupVideoCallingSetting')
      ),
      label: 'Group Video Calling',
      lockDesc: isLocked?.('groupVideoCallingSetting') ? BlockedByPersonaText : undefined,
      subLabel: 'Allow users to make and receive group video calls.',
      settingControl: renderCheckbox({
        userSettingKey: 'groupVideoCallingSetting',
        disabled: !(
          userSettings.isVideoCallingEnabled && getIsIndividual('groupVideoCallingSetting')
        ),
        dataTestId: 'group_video_calling',
      }),
      personaSettingControl: renderCheckbox({
        userSettingKey: 'groupVideoCallingSetting',
        disabled: !(
          userOrgSettings.isVideoCallEnabled && getIsIndividual('groupVideoCallingSetting')
        ),
        dataTestId: 'group_video_calling',
      }),
    },
    {
      requiredOrgSettings: ['featureDeliveryEscalation'],
      label: 'Delivery Escalations',
      lockDesc: isLocked?.('areDeliveryEscalationsEnabled') ? BlockedByPersonaText : undefined,
      subLabel: 'Enable SMS delivery if TigerConnect messages are undeliverable after 5 minutes.',
      settingControl: renderCheckbox({
        userSettingKey: 'areDeliveryEscalationsEnabled',
        dataTestId: 'delivery_escalations',
      }),
    },
    {
      requiredOrgSettings: ['featurePriorityMessaging'],
      requiredIndividualCondition: 'priorityMessagingSetting',
      label: 'Priority Messaging',
      lockDesc: isLocked?.('priorityMessagingSetting') ? BlockedByPersonaText : undefined,
      subLabel: 'Enable users to send high priority messages.',
      settingControl: renderCheckbox({
        userSettingKey: 'priorityMessagingSetting',
        dataTestId: 'priority_messaging',
      }),
    },
    {
      requiredOrgSettings: ['featureEnterprisePbx'],
      forbiddenOffCondition: 'pbxIntegrationSetting',
      hideFromPersonaForm: !getIsIndividual('pbxIntegrationSetting'),
      show: !!(urlParams.allowPbxIntegration || featureFlags?.pbx),
      label: 'Enterprise PBX Integration',
      lockDesc: isLocked?.('pbxIntegrationSetting') ? BlockedByPersonaText : undefined,
      subLabel:
        'Enable users to access PBX calling capabilities within TigerConnect. (Requires PBX enterprise network integration)',
      settingControl: renderCheckbox({
        userSettingKey: 'enterprisePbxSetting',
        disabled: !getIsIndividual('pbxIntegrationSetting'),
        dataTestId: 'enterprise_pbx_integration',
      }),
    },
    {
      requiredOrgSettings: ['featureTeams'],
      requiredIndividualCondition: 'teamsSetting',
      label: 'Teams',
      lockDesc: isLocked?.('teamsSetting') ? BlockedByPersonaText : undefined,
      subLabel: 'Allow users to create and activate teams',
      settingControl: renderCheckbox({ userSettingKey: 'teamsSetting', dataTestId: 'teams' }),
    },
    {
      requiredOrgSettings: ['featurePatientContext'],
      requiredIndividualCondition: 'patientContextSetting',
      label: 'Patient Reference',
      lockDesc: isLocked?.('patientContextSetting') ? BlockedByPersonaText : undefined,
      subLabel: 'Allow users to start a group message about a specific patient.',
      settingControl: renderCheckbox({
        userSettingKey: 'patientContextSetting',
        dataTestId: 'patient_reference',
      }),
    },
    {
      requiredOrgSettings: ['featureTigerSchedule'],
      requiredIndividualCondition: 'tigerScheduleSetting',
      label: 'TigerConnect Physician Scheduling',
      lockDesc: isLocked?.('tigerScheduleSetting') ? BlockedByPersonaText : undefined,
      subLabel: 'Allow users to access TigerConnect Physician Scheduling applications.',
      settingControl: renderCheckbox({
        userSettingKey: 'tigerScheduleSetting',
        dataTestId: 'tiger_schedule',
      }),
    },
    {
      requiredIndividualCondition: 'multiFactorAuthSetting',
      show: !!(featureFlags?.MFA || urlParams.allowMfa),
      hideFromPersonaForm: !getIsIndividual('multiFactorAuthSetting'),
      label: 'MFA Authentication',
      lockDesc: isLocked?.('multiFactorAuthSetting') ? BlockedByPersonaText : undefined,
      subLabel: 'Will enable the MFA Authentication process during login for this user.',
      settingControl: renderCheckbox({
        userSettingKey: 'multiFactorAuthSetting',
        disabled: !getIsIndividual('multiFactorAuthSetting'),
        dataTestId: 'mfa_authentication',
      }),
    },
  ];

  const patientEngagementSettings: RenderSettingsParams[] = [
    {
      requiredOrgSettings: ['featurePatientEngagement'],
      requiredIndividualCondition: 'patientEngagementSetting',
      label: 'TigerConnect Patient Engagement',
      lockDesc: isLocked?.('patientEngagementSetting') ? BlockedByPersonaText : undefined,
      subLabel:
        'Enable this feature to allow your users to communicate with patients. You can choose to enable this feature for your entire organization or enable this feature to individual users.',
      settingControl: renderCheckbox({
        userSettingKey: 'patientEngagementSetting',
        dataTestId: 'patient_engagement',
      }),
    },
    {
      requiredOrgSettings: ['featurePatientQuickAdd'],
      requiredIndividualCondition: 'patientQuickAddSetting',
      label: 'Patient Quick Add',
      lockDesc: isLocked?.('patientQuickAddSetting') ? BlockedByPersonaText : undefined,
      hideFromPersonaForm: !(
        userSettings.patientEngagementSetting || userOrgSettings.patientEngagementSetting === 'all'
      ),
      subLabel: 'Allow users to quickly add a patient from their mobile application.',
      settingControl: renderCheckbox({
        userSettingKey: 'patientQuickAddSetting',
        disabled: !(
          userSettings.patientEngagementSetting ||
          userOrgSettings.patientEngagementSetting === 'all'
        ),
        dataTestId: 'patient_quick_add',
      }),
    },
    {
      requiredOrgSettings: ['featurePatientQuickCall'],
      requiredIndividualCondition: 'patientQuickCallSetting',
      label: 'Patient Quick Call',
      lockDesc: isLocked?.('patientQuickCallSetting') ? BlockedByPersonaText : undefined,
      hideFromPersonaForm: !(
        userSettings.patientEngagementSetting || userOrgSettings.patientEngagementSetting === 'all'
      ),
      subLabel:
        'Allow users to quickly dial any telephone number using masked cellular calling from their mobile application.',
      settingControl: renderCheckbox({
        userSettingKey: 'patientQuickCallSetting',
        disabled: !(
          userSettings.patientEngagementSetting ||
          userOrgSettings.patientEngagementSetting === 'all'
        ),
        dataTestId: 'patient_quick_call',
      }),
    },
    {
      requiredOrgSettings: ['featurePatientQuickCallVideo'],
      requiredIndividualCondition: 'patientQuickCallVideoSetting',
      label: 'Patient Quick Call Video',
      lockDesc: isLocked?.('patientQuickCallVideoSetting') ? BlockedByPersonaText : undefined,
      subLabel:
        'Allow users to quickly make video calls to patients from their mobile application.',
      settingControl: renderCheckbox({
        userSettingKey: 'patientQuickCallVideoSetting',
        dataTestId: 'patient_quick_call_video',
      }),
    },
    {
      requiredOrgSettings: ['featurePatientVoip'],
      requiredIndividualCondition: 'patientVoiceCallingSetting',
      show: !!urlParams.pfVoiceCall,
      label: 'Voice Calling',
      lockDesc: isLocked?.('patientVoiceCallingSetting') ? BlockedByPersonaText : undefined,
      subLabel: 'Enable users to voice call with patients.',
      settingControl: renderCheckbox({
        userSettingKey: 'patientVoiceCallingSetting',
        dataTestId: 'voice_calling',
      }),
    },
    {
      requiredOrgSettings: ['featureGroupAudioCalling'],
      forbiddenOffCondition: 'groupAudioCallingSetting',
      show: !!urlParams.pfGroupVoiceCall,
      label: 'Group Voice Calling',
      lockDesc: isLocked?.('patientGroupVoiceCallingSetting') ? BlockedByPersonaText : undefined,
      subLabel: 'Enable users to group voice call with patients.',
      settingControl: renderCheckbox({
        userSettingKey: 'patientGroupVoiceCallingSetting',
        dataTestId: 'group_voice_calling',
      }),
    },
    {
      requiredOrgSettings: ['featureVideoCall'],
      requiredIndividualCondition: 'patientVideoCallingSetting',
      label: 'Video Calling',
      lockDesc: isLocked?.('patientVideoCallingSetting') ? BlockedByPersonaText : undefined,
      subLabel: 'Enable users to video call with patients.',
      settingControl: renderCheckbox({
        userSettingKey: 'patientVideoCallingSetting',
        dataTestId: 'video_calling',
      }),
    },
    {
      requiredOrgSettings: ['featurePatientGroupVideoCalling'],
      requiredIndividualCondition: 'patientGroupVideoCallingSetting',
      label: 'Group Video Calling',
      lockDesc: isLocked?.('patientGroupVideoCallingSetting') ? BlockedByPersonaText : undefined,
      subLabel: 'Enable users to group video call with patients.',
      settingControl: renderCheckbox({
        userSettingKey: 'patientGroupVideoCallingSetting',
        dataTestId: 'group_video_calling',
      }),
    },
    {
      requiredOrgSettings: ['featurePatientEngagement'],
      requiredIndividualCondition: 'patientListAccessSetting',
      label: 'Patient List Access',
      lockDesc: isLocked?.('patientListAccessSetting') ? BlockedByPersonaText : undefined,
      subLabel: 'Allow providers to access and update patient information in the Patient List.',
      settingControl: renderCheckbox({
        userSettingKey: 'patientListAccessSetting',
        dataTestId: 'patient_list_access',
      }),
    },
    {
      requiredOrgSettings: ['featurePatientScheduledMessages'],
      requiredIndividualCondition: 'patientScheduledMessagesSetting',
      label: 'Schedule Messages',
      lockDesc: isLocked?.('patientScheduledMessagesSetting') ? BlockedByPersonaText : undefined,
      subLabel: 'Allow providers to schedule messages to patients.',
      settingControl: renderCheckbox({
        userSettingKey: 'patientScheduledMessagesSetting',
        dataTestId: 'schedule_messages',
      }),
    },
    {
      requiredOrgSettings: ['featurePatientVirtualWaitingRoom'],
      forbiddenOffCondition: 'patientVirtualWaitingRoomSetting',
      hideFromPersonaForm: !getIsIndividual('patientVirtualWaitingRoomSetting'),
      label: 'Virtual Waiting Room',
      lockDesc: isLocked?.('virtualWaitingRoomSetting') ? BlockedByPersonaText : undefined,
      subLabel:
        'Will enable the ability for this user to be given access to a virtual waiting room.',
      settingControl: renderCheckbox({
        userSettingKey: 'virtualWaitingRoomSetting',
        disabled: !getIsIndividual('patientVirtualWaitingRoomSetting'),
        dataTestId: 'virtual_waiting_room',
      }),
    },
    {
      requiredOrgSettings: ['featurePatientBroadcasting'],
      requiredIndividualCondition: 'patientBroadcastingSetting',
      label: 'Patient Broadcasting',
      lockDesc: isLocked?.('patientBroadcastingSetting') ? BlockedByPersonaText : undefined,
      subLabel: 'Allow providers to broadcast messages to patients.',
      settingControl: renderCheckbox({
        userSettingKey: 'patientBroadcastingSetting',
        dataTestId: 'patient_broadcasting',
      }),
    },
  ];
  return { settings, patientEngagementSettings };
};

export const getUserPermissions = async (
  orgId: string,
  userId: string,
  orgSettings?: OrganizationSettings | undefined
) => {
  let settings = orgSettings;
  const _permissions = await TCClient.adminUsers.getUserPermissions({
    orgId,
    userId,
  });
  if (!settings) {
    settings = await TCClient.adminOrganizations.fetchSettings({
      organizationId: orgId,
    });
  }
  const { featureCustomFastDeploy } = settings;
  if (!featureCustomFastDeploy) delete _permissions['customer'];
  return _permissions;
};
